import { useState, useEffect } from "react";
import axios from "axios";
import Header from "../components/ui/header";
import "../design/dashboard.css";
import { CardDetails } from "../components/ui/details";
import { CardContent } from "../components/ui/card";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Footer from "../components/ui/footer";

export default function Dashboard() {
    const [tasks, setTasks] = useState([]);
    const [savedProperties, setSavedProperties] = useState([]); 
    const [editingTask, setEditingTask] = useState(null);
    const [editedTaskName, setEditedTaskName] = useState("");
    const [newTaskName, setNewTaskName] = useState(""); 
    const token = localStorage.getItem("access_token");
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const default_tags = ["Contacted", "Closed", "Follow Up", "Ignore", "Priority"]; 
    const [selectedTags, setSelectedTags] = useState([]);
    const [, setProperties] = useState([]);
    const [customTag, setCustomTag] = useState("");
    const [notes, setNotes] = useState(""); 
    const [noteSavedMessage, setNoteSavedMessage] = useState(""); 
    const [noteErrorMessage, setNoteErrorMessage] = useState("");
    const [, setSaved] = useState(false);
    const [phone_number, setPhone] = useState(""); 
    const [email, setEmail] = useState("");  
    const [isEditing, setIsEditing] = useState(false);

    const openDetails = async (property) => {
        try {
          const propertyResponse = await fetch(
            `https://api.deliquentauction.com/properties/property/?search_address=${encodeURIComponent(property.search_address)}`,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
              },
            }
          );
      
          if (!propertyResponse.ok) {
            throw new Error("Failed to fetch property data");
          }
      
          const fetchedProperty = await propertyResponse.json();
      
          const userDataResponse = await fetch(
            `https://api.deliquentauction.com/properties/property/user-data/?search_address=${encodeURIComponent(property.search_address)}`,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
              },
            }
          );
      
          if (!userDataResponse.ok) {
            throw new Error("Failed to fetch user-specific data");
          }
      
          const userData = await userDataResponse.json();
      
          const updatedProperty = {
            ...fetchedProperty,
            notes: userData.notes || "",
            tags: userData.tags || [],
            saved: userData.saved || false,
            phone_number: userData.phone_number || "",
            email: userData.email || "",
          };
      
          setSelectedProperty(updatedProperty);
          setSelectedTags(updatedProperty.tags || []);
          setNotes(updatedProperty.notes || "");
          setSaved(updatedProperty.saved || false);
          setPhone(updatedProperty.phone_number || "");
          setEmail(updatedProperty.email || "");
      
          setIsDetailsOpen(true);
        } catch (error) {
          console.error("Error fetching property details:", error);
        }
      };        

    const closeDetails = (property) => {
        setIsDetailsOpen(false);
        setSelectedProperty(null);
    };

    useEffect(() => {
        if (!token) return;
      
        const fetchTasksAndProperties = async () => {
          try {
            const taskResponse = await axios.get("https://api.deliquentauction.com/tasks/", {
              headers: { Authorization: `Bearer ${token}` },
            });
            setTasks(taskResponse.data);
      
            const savedPropertiesResponse = await axios.get(
              "https://api.deliquentauction.com/properties/saved/",
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            setSavedProperties(savedPropertiesResponse.data);
          } catch (error) {
            console.error("Error fetching tasks and properties", error);
          }
        };
      
        fetchTasksAndProperties();
      }, [token]);      

    const deleteTask = async (taskId) => {
    try {
        await axios.delete(`https://api.deliquentauction.com/tasks/${taskId}/`, {
        headers: { Authorization: `Bearer ${token}` },
        });
        setTasks(tasks.filter((task) => task.id !== taskId));
    } catch (error) {
        console.error("Error deleting task", error);
    }
    };

    const startEditing = (task) => {
        setEditingTask(task.id);
        setEditedTaskName(task.task_name);
    };

    const cancelEditing = () => {
        setEditingTask(null);
        setEditedTaskName("");
    };

    const saveTask = async (taskId) => {
    try {
        const response = await axios.put(
        `https://api.deliquentauction.com/tasks/${taskId}/`,
        { task_name: editedTaskName },
        { headers: { Authorization: `Bearer ${token}` } }
        );

        setTasks(tasks.map((task) => (task.id === taskId ? response.data : task)));
        setEditingTask(null);
        setEditedTaskName("");
    } catch (error) {
        console.error("Error updating task", error);
    }
    };

    const addTask = async () => {
    if (newTaskName.trim() === "") return; 

    try {
        const response = await axios.post(
            "https://api.deliquentauction.com/tasks/",
            { task_name: newTaskName },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        setTasks([...tasks, response.data]);
        setNewTaskName(""); 
    } catch (error) {
        console.error("Error adding task", error);
    }
    };

    const unsaveProperty = async (search_address) => {
        try {
          const response = await fetch(`https://api.deliquentauction.com/properties/property/user-data/?search_address=${encodeURIComponent(search_address)}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              search_address,
              saved: false,
            }),
          });
      
          if (!response.ok) {
            throw new Error("Failed to unsave property");
          }
      
          setSavedProperties((prev) =>
            prev.filter((property) => property.search_address !== search_address)
          );
        } catch (error) {
          console.error("Error unsaving property", error);
        }
      };      
      
    const addTag = (tag) => {
        if (!selectedTags.includes(tag)) {
        const updatedTags = [...selectedTags, tag];
            setSelectedTags(updatedTags);
            setProperties((prevProperties) =>
                prevProperties.map((prop) =>
                prop.search_address === selectedProperty.search_address
                    ? { ...prop, tags: updatedTags }
                    : prop
                )
            );
            updateTagsDatabase(updatedTags);
        }
    };

    const RemoveTag = (tag) => {
        const updatedTags = selectedTags.filter(t => t !== tag);
        setSelectedTags(updatedTags);
        setProperties((prevProperties) =>
        prevProperties.map((prop) =>
            prop.search_address === selectedProperty.search_address
            ? { ...prop, tags: updatedTags }
            : prop
        )
        );
        updateTagsDatabase(updatedTags);
    };

    const newCustomTag = (e) => {
        e.preventDefault();
        if (customTag.trim() !== "") {
            addTag(customTag.trim());
            setCustomTag(""); 
        }
    };

    const updateTagsDatabase = async (tags) => {
        try {
            const response = await fetch(
                `https://api.deliquentauction.com/properties/property/user-data/?search_address=${encodeURIComponent(selectedProperty.search_address)}`,
            {
                method: "PUT",
                headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                search_address: selectedProperty.search_address,
                tags,
                }),
            }
            );

            if (!response.ok) {
            console.error("Failed to update tags");
            }
        } catch (error) {
            console.error("Error updating tags:", error);
        }
    };
  
    const saveNotes = async () => {
        try {
          const response = await fetch(
            `https://api.deliquentauction.com/properties/property/user-data/?search_address=${encodeURIComponent(selectedProperty.search_address)}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                search_address: selectedProperty.search_address,
                notes,
              }),
            }
          );
      
          if (!response.ok) {
            throw new Error("Failed to save notes");
          }
      
          setNoteSavedMessage("Note Saved.");
          setNoteErrorMessage("");
          setTimeout(() => setNoteSavedMessage(""), 2000);
        } catch (error) {
          console.error("Error saving notes", error);
          setNoteErrorMessage("Failed to save the note. Please try again.");
          setTimeout(() => setNoteErrorMessage(""), 4000);
        }
      };
      
    const customIcon = new L.Icon({
        iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
    });

    const saveContactInfo = async () => {
        try {
        await upsertUserPropertyData({ phone_number, email });

        setSelectedProperty((prev) => ({
            ...prev,
            phone_number: phone_number,
            email: email,
        }));

        setIsEditing(false);
        } catch (error) {
        console.error("Error saving contact info:", error);
        }
    };  

    const upsertUserPropertyData = async (newData = {}) => {
        try {
        await fetch(`https://api.deliquentauction.com/properties/property/user-data/?search_address=${encodeURIComponent(selectedProperty.search_address)}`, {
            method: "PUT",
            headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
            search_address: selectedProperty.search_address,
            notes,
            tags: selectedTags,
            saved: selectedProperty.saved,
            phone_number,
            email,
            ...newData,
            }),
        });
        } catch (error) {
        console.error("Error updating user property data:", error);
        }
    };

    return (
        <div className="dashboard">
        <Header />

        <div className="card-body-dash">

            <div className="task-list">

                <div className="task-header">
                    <h2>Tasks</h2>
                </div>

                <div className="add-task">
                    <div className="add-task-body">
                        <input
                            type="text"
                            placeholder="New Task"
                            value={newTaskName}
                            onChange={(e) => setNewTaskName(e.target.value)}
                        />
                        <button onClick={addTask} className="add-task-btn">Add Task</button>
                    </div>
                </div>
                
                <div className="task-display">
                    {tasks.length === 0 ? (
                    <p>No tasks available.</p>
                    ) : (
                    <div>
                        {tasks.map((task) => (
                        <div key={task.id} className="task-item">
                            {editingTask === task.id ? (
                            <>
                                <input
                                type="text"
                                value={editedTaskName}
                                onChange={(e) => setEditedTaskName(e.target.value)}
                                />
                                <button onClick={() => saveTask(task.id)} className="save-task-btn">✔</button>
                                <button onClick={cancelEditing} className="cancel-task-btn">✖</button>
                            </>
                            ) : (
                            <>
                                <span>{task.task_name}</span>
                                <button onClick={() => startEditing(task)} className="edit-task-btn">✎</button>
                                <button onClick={() => deleteTask(task.id)} className="delete-task-btn">✖</button>
                            </>
                            )}
                        </div>
                        ))}
                    </div>
                    )}
                </div>
            </div>

            <div className="saved-properties">
                
                <div className="saved-properties-header">
                    <h2>Saved Properties</h2>
                </div>

                {savedProperties.length === 0 ? (
                <p>No saved properties available.</p>
                ) : (
                <div className="properties-list">
                    {savedProperties.map((property) => (
                    <div key={property.search_address} className="property-item">
                        <span onClick={() => openDetails(property)}>
                            {property.full_address}
                        </span>
                        <button onClick={() => unsaveProperty(property.search_address)} className="unsave-btn">✖</button>
                    </div>
                    ))}
                </div>
                )}
            </div>

            <CardDetails isOpen={isDetailsOpen} onClose={closeDetails}>
                {selectedProperty && (
                <CardContent>

                    <div className="card-header-max">
                        <h2>{selectedProperty.full_address}</h2>
                        <span className={`status-badge ${selectedProperty.status?.toLowerCase()}`}>{selectedProperty.status}</span>
                    </div>

                    <div className="max-card-body">

                    <details className="tag-details">
                        <summary>Tags</summary>

                        <div className="details-container">

                            <div className="max-card-detail">
                                <h3>Manage Tags</h3>
                                {default_tags.map((tag) => (
                                    <button 
                                    key={tag} 
                                    onClick={() => {
                                        selectedTags.includes(tag) ? RemoveTag(tag) : addTag(tag);
                                    }}
                                    className={selectedTags.includes(tag) ? "active" : ""}
                                    >
                                    {selectedTags.includes(tag) ? `${tag}` : tag}
                                    </button>
                                ))}
                                <form onSubmit={newCustomTag}>
                                    <input
                                        type="text"
                                        value={customTag}
                                        onChange={(e) => setCustomTag(e.target.value)}
                                        placeholder="Enter custom tag"
                                    />
                                    <button type="submit">Add</button>
                                </form>
                            </div>
                            <div className="max-card-detail">
                                <h3>Current Tags</h3>
                                {Array.isArray(selectedTags) && selectedTags.length > 0 ? (
                                selectedTags.map((tag, index) => (
                                    <span key={index} className="tag-item">
                                    {tag}
                                    <button onClick={() => RemoveTag(tag)}>✖</button>
                                    </span>
                                ))
                                ) : (
                                <span>No Tags</span>
                                )}
                            </div>
                        </div>
                    </details>

                    <details className="auction-details" open>
                        <summary>Auction Information</summary>
                        
                        <div className="details-container">
                        
                        <div className="max-card-detail">
                            <h3>Auction Date</h3>
                            <p>{new Date(selectedProperty.sale_date).toLocaleDateString()}</p>
                        </div>
                        
                        <div className="max-card-detail">
                            <h3>Opening Bid</h3>
                            <p>{selectedProperty.opening_bid && selectedProperty.opening_bid.toLowerCase() === "nan" ? "$0" : selectedProperty.opening_bid}</p>
                        </div>
                        
                        <div className="max-card-detail">
                            <h3>Parcel Value</h3>
                            <p>{selectedProperty.parcel_value.toLowerCase() === "nan" ? "$0" : `${selectedProperty.parcel_value}`}</p>
                        </div>

                        <div className="max-card-detail">
                            <h3>Judgment</h3>
                            <p>{selectedProperty.judgment.toLowerCase() === "nan" ? "$0" : `${selectedProperty.judgment}`}</p>
                        </div>
                        
                        <div className="max-card-detail">
                            <h3>Distress List</h3>
                            <p>{selectedProperty.industry}</p>
                        </div>
                        </div>
                    </details>
                    
                    <details className="parcel-details" open>
                        <summary>Parcel Information</summary>
                        
                        <div className="details-container">
                        
                            <div className="max-card-detail">
                                <h3>County</h3>
                                <p>{selectedProperty.county}</p>
                            </div>
                            
                            <div className="max-card-detail">
                                <h3>City</h3>
                                <p>{selectedProperty.parcel_city}</p>
                            </div>
                            
                            <div className="max-card-detail">
                                <h3>Parcel ID</h3>
                                <p>{selectedProperty.parcel_id}</p>
                            </div>
                            
                            <div className="max-card-detail">
                                <h3>Case Number</h3>
                                <p>{selectedProperty.case_number}</p>
                            </div>
                            
                            <div className="max-card-detail">
                                <h3>Measurements</h3>
                                <p>{selectedProperty.measurements}</p>
                            </div>
                            
                            <div className="max-card-detail">
                                <h3>Zoning Type</h3>
                                <p>{selectedProperty.zoning_type && selectedProperty.zoning_type.toLowerCase() === "nan" ? "N/A" : `${selectedProperty.zoning_type}`}</p>
                            </div>
                        </div>
                        <div className="search-address">
                            <h3>Search Address</h3>
                            <p>{selectedProperty.search_address}</p>
                        </div>
                    </details>
                    
                    <details className="owner-details" open>
                        <summary>Owner Information</summary>
                        
                        <div className="details-container">
                        
                        <div className="max-card-detail">
                            <h3>Owner</h3>
                            <p>{selectedProperty.owner}</p>
                        </div>
                        
                        <div className="max-card-detail">
                            <h3>Mailing Address</h3>
                            <p>{selectedProperty.mailing_address}</p>
                        </div>

                        <div className="max-card-detail">
                            <h3>Phone</h3>
                            {isEditing ? (
                                <input 
                                    type="tel"
                                    value={phone_number}
                                    placeholder="Enter phone number here..."
                                    onChange={(e) => setPhone(e.target.value)}
                                />
                            ) : (
                                <p>{selectedProperty?.phone_number}</p>
                            )}
                            <button onClick={() => setIsEditing((prev) => !prev)}>
                                {isEditing ? "Cancel" : "Edit Contact"}
                            </button>
                            {isEditing && (
                            <button onClick={saveContactInfo}>Save</button>
                            )}
                        </div>
                        <div className="max-card-detail">
                            <h3>Email</h3>
                            {isEditing ? (
                            <input
                            type="email"
                            value={email}
                            placeholder="Enter email address here..."
                            onChange={(e) => setEmail(e.target.value)}
                            />
                            ) : (
                            <p>{selectedProperty?.email}</p>
                            )}
                        </div>
                        </div>
                    </details>

                    <details className="notes">
                        <summary>Notes</summary>
                        
                        <div className="details-container">
                        
                        <div className="max-card-detail">
                            <h3>Notes</h3>
                            <textarea 
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add notes here..."
                            />
                            <button onClick={saveNotes} disabled={!notes}>Save Notes</button>
                            {noteSavedMessage && (
                            <p className="note-saved-message">{noteSavedMessage}</p>
                            )}
                            {noteErrorMessage && (
                            <p className="note-error-message">{noteErrorMessage}</p>
                            )}
                        </div>
                        </div>
                    </details>
                    </div>
                    
                    <details className="map-details">
                    <summary>Map</summary>
                        
                        <div className="details-container">
                        
                        <div className="map">
                            {selectedProperty.latitude && selectedProperty.longitude && (
                            <div className="map-container">
                                <MapContainer
                                center={[selectedProperty.latitude, selectedProperty.longitude]}
                                zoom={15}
                                style={{ height: "300px", width: "100%", borderRadius: "10px" }}
                                >
                                <TileLayer
                                    url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                                />
                                <Marker position={[selectedProperty.latitude, selectedProperty.longitude]} icon={customIcon}>
                                    <Popup>{selectedProperty.full_address}</Popup>
                                </Marker>
                                </MapContainer>
                            </div>
                            )}
                        </div>
                        </div>
                    </details>
                </CardContent>
                )}
            </CardDetails>
        </div>
        <Footer />
        </div>
    );
    }